import { resetDemoCentering } from './scroll-geometry'
import { DEMO_CALCULATOR_STEP, DEMO_FIELD_TYPE } from 'config/constants'
import { centerDemoField, trackDemoField } from './center-field'
import { DemoPlayerContext, getDemoPlayerClock, setDemoPlayerClock } from './player-clock'
import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import Typography from '@mui/material/Typography'

import {
  DEMO_ANIMATION_START_STEP,
  DEMO_CALCULATOR_STEPS,
  DEMO_INITIAL_DELAY,
  DEMO_PLAYBACK_END_EVENT,
  DEMO_PLAYBACK_START_EVENT,
  DEMO_SCROLL_DELAY,
  DEMO_STEP_DELAY,
} from './playback-constants'
import { playDemoFields } from './play-fields'
import {
  prepareFields,
  restoreFields,
  restoreStaticValues,
  scrollToDemoPlayerElement,
  wait,
  waitForStepData,
  waitForStepElement,
} from './helpers'
import { playAdditionalServicesStep } from './steps/additional-services/playback'
import { playBankCommissionStep } from './steps/payment/playback'
import { playDeliveryStep } from './steps/delivery/playback'
import { playPackagingStep } from './steps/packaging/playback'
import { playMainStep } from './steps/main/playback'
import { playCustomsStep } from './steps/customs/playback'
import { playCertificationStep } from './steps/certification/playback'

import './playback.scss'

export * from './playback-constants'
export * from './playback-hooks'
export * from './play-fields'

const DemoStepPlayback = ({
  onCompleteAdditionalServicesStep,
  onCompleteBankCommissionStep,
  onCompleteDeliveryStep,
  onCompleteFirstStep,
  onCompletePackagingStep,
}: DemoStepPlaybackProps) => {
  const player = useContext(DemoPlayerContext)

  const [ animatedFields, setAnimatedFields ] = useState<AnimatedField[]>([])

  const [ activeFieldElement, setActiveFieldElement ] = useState<Element | null>(null)

  const activeFieldsRef = useRef<AnimatedField[]>([])

  const activeInvoiceValuesRef = useRef<StaticValueState[]>([])

  useLayoutEffect(() => {
    if (activeFieldElement) return trackDemoField(activeFieldElement)
  }, [ activeFieldElement, player?.clock ])

  useEffect(() => {
    let cancelled = false
    const clock = player?.clock

    if (clock) {
      clock.controller = new AbortController()
    }

    setDemoPlayerClock(clock)
    const playbackId = `${Date.now()}-${Math.random()}`

    const isCurrentPlayback = () => (
      !cancelled && document.documentElement.dataset.demoPlaybackId === playbackId
    )

    const dispatchPlaybackEvent = (event: Event) => {
      if (!isCurrentPlayback()) {
        return false
      }

      window.dispatchEvent(event)

      return true
    }

    const play = async () => {
      const content = document.querySelector<HTMLElement>('.demo__content')
      if (player && content) resetDemoCentering(content)
      document.documentElement.dataset.demoPlaybackId = playbackId
      document.documentElement.dataset.demoPlaybackActive = 'true'
      dispatchPlaybackEvent(new Event(DEMO_PLAYBACK_START_EVENT))
      let currentStep = DEMO_ANIMATION_START_STEP

      const showFields = (fields: AnimatedField[]) => {
        activeFieldsRef.current = fields
        setAnimatedFields(fields)
      }

      const clearFields = () => {
        restoreFields(activeFieldsRef.current)
        showFields([])
        setActiveFieldElement(null)
      }

      const controls: PlaybackControls = {
        clearFields,
        dispatchEvent: dispatchPlaybackEvent,
        isCancelled: () => cancelled,
        setActiveFieldElement,
        showFields,
        typeFields: (fields) => playDemoFields({
          controls,
          fields: fields.map(field => ({
            animatedField: field,
            element: field.element,
            name: field.element.name,
            shouldScroll: !field.element.closest('.invoice-form'),
            type: DEMO_FIELD_TYPE.TEXTFIELD,
            value: field.targetValue,
          })),
          step: currentStep,
          visibleFields: activeFieldsRef.current,
        }),
      }

      await wait(player ? 0 : DEMO_INITIAL_DELAY)

      const firstStepIndex = player?.startStep ?? DEMO_CALCULATOR_STEPS.indexOf(DEMO_ANIMATION_START_STEP)

      for (const step of DEMO_CALCULATOR_STEPS.slice(firstStepIndex)) {
        currentStep = step

        if (cancelled) {
          return
        }

        const stepElement = await waitForStepElement(step, () => cancelled)

        if (!stepElement) {
          return
        }

        const startsAtNaturalTop = Boolean(player) && (
          step === DEMO_CALCULATOR_STEP.MAIN || step === DEMO_CALCULATOR_STEP.PACKAGING
        )
        if (startsAtNaturalTop && content) {
          resetDemoCentering(content)
          content.scrollTop = 0
        }

        if ([ DEMO_CALCULATOR_STEP.MAIN, DEMO_CALCULATOR_STEP.PACKAGING, DEMO_CALCULATOR_STEP.BANKING_COMMISSIONS ].includes(step)) {
          const hasStepData = await waitForStepData(stepElement, () => cancelled)

          if (!hasStepData) {
            return
          }
        }

        let shouldStartNextStepImmediately = false
        const fields = prepareFields(stepElement)

        controls.showFields(fields)

        if (!player && step === DEMO_CALCULATOR_STEP.CUSTOMS) {
          await wait(DEMO_STEP_DELAY)
        }

        let scrollBehavior: ScrollBehavior = 'smooth'

        if (step === DEMO_CALCULATOR_STEP.DELIVERY) {
          scrollBehavior = 'auto'
        }

        const stepHasContinuousScroll = [
          DEMO_CALCULATOR_STEP.CUSTOMS,
          DEMO_CALCULATOR_STEP.CERTIFICATION,
          DEMO_CALCULATOR_STEP.BANKING_COMMISSIONS,
        ].includes(step)
        const stepHandlesPlayerScroll = Boolean(player) && stepHasContinuousScroll
        const shouldPositionStep = !stepHandlesPlayerScroll && !startsAtNaturalTop
        const shouldScrollToHeading = [ DEMO_CALCULATOR_STEP.CUSTOMS, DEMO_CALCULATOR_STEP.CERTIFICATION, DEMO_CALCULATOR_STEP.BANKING_COMMISSIONS, DEMO_CALCULATOR_STEP.ADDITIONAL_SERVICES ].includes(step)
        const shouldScrollSmoothly = shouldPositionStep && shouldScrollToHeading
        const stepScrollTarget = shouldScrollToHeading
          ? stepElement.querySelector<HTMLElement>('h3') || stepElement
          : stepElement
        const scrolledInPlayer = shouldScrollSmoothly &&
          await scrollToDemoPlayerElement({ element: stepScrollTarget, isCancelled: controls.isCancelled })

        if (cancelled) {
          return
        }

        if (shouldPositionStep && !scrolledInPlayer) {
          if (!centerDemoField(stepScrollTarget)) {
            stepScrollTarget.scrollIntoView({ behavior: scrollBehavior, block: 'start' })
          }
        }

        if (scrolledInPlayer) {
          await wait(DEMO_STEP_DELAY * 2)
        }

        if (!player && step !== DEMO_CALCULATOR_STEP.DELIVERY) {
          await wait(DEMO_SCROLL_DELAY)
        }

        if (step === DEMO_CALCULATOR_STEP.DELIVERY) {
          const deliveryIsComplete = await playDeliveryStep({
            controls,
            fields,
            onComplete: onCompleteDeliveryStep,
            stepElement,
          })

          if (!deliveryIsComplete) {
            return
          }

          continue
        }

        if (step === DEMO_CALCULATOR_STEP.BANKING_COMMISSIONS) {
          const bankCommissionsAreComplete = await playBankCommissionStep({
            controls,
            fields,
            onComplete: onCompleteBankCommissionStep,
            stepElement,
          })

          if (!bankCommissionsAreComplete) {
            return
          }

          continue
        }

        if (step === DEMO_CALCULATOR_STEP.ADDITIONAL_SERVICES) {
          const additionalServicesAreComplete = await playAdditionalServicesStep({
            controls,
            fields,
            onComplete: onCompleteAdditionalServicesStep,
            stepElement,
          })

          if (!additionalServicesAreComplete) {
            return
          }

          continue
        }

        if (step === DEMO_CALCULATOR_STEP.PACKAGING) {
          const packagingIsComplete = await playPackagingStep({
            activeInvoiceValuesRef,
            controls,
            fields,
            onComplete: onCompletePackagingStep,
            stepElement,
          })

          if (!packagingIsComplete) {
            return
          }

          shouldStartNextStepImmediately = true
        } else if (step === DEMO_CALCULATOR_STEP.MAIN) {
          const mainIsComplete = await playMainStep({
            controls,
            fields,
            onComplete: onCompleteFirstStep,
            stepElement,
          })

          if (!mainIsComplete) {
            return
          }
        } else if (step === DEMO_CALCULATOR_STEP.CUSTOMS) {
          const customsIsComplete = await playCustomsStep({
            controls,
            fields,
            stepElement,
          })

          if (!customsIsComplete) {
            return
          }
        } else if (step === DEMO_CALCULATOR_STEP.CERTIFICATION) {
          const certificationIsComplete = await playCertificationStep({
            controls,
            fields,
            stepElement,
          })

          if (!certificationIsComplete) {
            return
          }
        }

        if (!shouldStartNextStepImmediately) {
          await wait(DEMO_STEP_DELAY)
        }

        setActiveFieldElement(null)

        restoreFields(fields)
        restoreStaticValues(activeInvoiceValuesRef.current)
        activeInvoiceValuesRef.current = []
        activeFieldsRef.current = []
        setAnimatedFields([])

        if (!shouldStartNextStepImmediately) {
          await wait(DEMO_STEP_DELAY)
        }
      }

      if (dispatchPlaybackEvent(new Event(DEMO_PLAYBACK_END_EVENT))) {
        player?.onComplete()
      }

      if (document.documentElement.dataset.demoPlaybackId === playbackId) {
        delete document.documentElement.dataset.demoPlaybackActive
        delete document.documentElement.dataset.demoPlaybackId
      }
    }

    play().catch(error => {
      if (error?.name !== 'AbortError') {
        console.error(error)
      }
    })

    return () => {
      const isLatestPlayback = document.documentElement.dataset.demoPlaybackId === playbackId

      const content = document.querySelector('.demo__content')
      if (player && content) resetDemoCentering(content)
      cancelled = true
      clock?.controller.abort()

      if (getDemoPlayerClock() === clock) {
        setDemoPlayerClock(undefined)
      }

      if (isLatestPlayback) {
        window.dispatchEvent(new Event(DEMO_PLAYBACK_END_EVENT))
        delete document.documentElement.dataset.demoPlaybackActive
        delete document.documentElement.dataset.demoPlaybackId
        delete document.documentElement.dataset.demoAdditionalFieldsPreparing
      }

      restoreFields(activeFieldsRef.current)
      restoreStaticValues(activeInvoiceValuesRef.current)
    }
  }, [
    player?.clock,
    player?.startStep,
    player?.onComplete,
    onCompleteAdditionalServicesStep,
    onCompleteBankCommissionStep,
    onCompleteDeliveryStep,
    onCompleteFirstStep,
    onCompletePackagingStep,
  ])

  return <>
    {animatedFields.map((field, index) => createPortal(
      <Typography
        component='span'
        sx={{ font: 'inherit', color: 'inherit', letterSpacing: 'inherit' }}
        aria-hidden='true'
        className='calculator-demo-typing-field__value'
        style={field.overlayStyle}
      >
        {field.visibleValue}
        {activeFieldElement === field.element && (
          <span className='calculator-demo-typing-field__caret' />
        )}
      </Typography>,
      field.root,
      `${field.element.name || field.targetValue}-${index}`,
    ))}
  </>
}

export default DemoStepPlayback
